import { getAucklandISOString } from "./utils";

// Get cookie by name
export const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
};

// HubSpot's visitor id: the hubspotutk cookie, or, when that is missing, the same id from
// HubSpot's __hstc cookie. __hstc reads "<domain hash>.<visitor id>.<timestamps>…", so a
// browser that still carries __hstc can be linked to its tracked session. Without either,
// the signup form cannot be linked to the visitor's browsing session.
export const getHubspotUTK = () => {
  const utk = getCookie("hubspotutk");
  if (utk) {
    return utk;
  }
  const visitorId = (getCookie("__hstc") || "").split(".")[1];
  return /^[0-9a-f]{32}$/i.test(visitorId || "") ? visitorId : null;
};

// Expires a cookie under every Domain it may have been set with. The setter decides the
// Domain (the bare host on localhost, a leading-dot root domain in prod), so this clears
// every variant rather than guessing one. An expired write against a domain that does not
// match the cookie's real one is a harmless no-op.
const expireCookie = (name) => {
  const expired = "expires=Thu, 01 Jan 1970 00:00:00 UTC";
  const host = window.location.hostname;
  const labels = host.split(".");
  const rootDomain = labels.length > 1 ? labels.slice(-2).join(".") : host;

  document.cookie = `${name}=; ${expired}; path=/`;
  new Set([host, `.${host}`, rootDomain, `.${rootDomain}`]).forEach(
    (domain) => {
      document.cookie = `${name}=; ${expired}; path=/; domain=${domain}`;
    }
  );
};

// HubSpot's visitor cookies. __hstc carries the same visitor id as hubspotutk (see
// getHubspotUTK), so clearing hubspotutk alone would leave the visitor identifiable.
const HUBSPOT_VISITOR_COOKIES = ["hubspotutk", "__hstc", "__hssc", "__hssrc"];

// Clears HubSpot's visitor cookies (FV2-946), called on sign-out, so the next visitor on this
// browser — a different real person on a shared device, or the next manual test signup —
// gets a fresh HubSpot visitor identity instead of inheriting this session's, which would
// otherwise get every later form submission from this browser cross-linked to this one.
export const clearHubspotUTK = () => {
  HUBSPOT_VISITOR_COOKIES.forEach(expireCookie);
};

// Context for the HubSpot Forms API submission (FV2-604): lets HubSpot stitch a new
// contact to this browser's tracked session. hutk is omitted (not empty-stringed) when
// absent so the submission still succeeds — just without session stitching.
export const buildHubspotFormContext = () => {
  const hutk = getHubspotUTK();

  return {
    ...(hutk ? { hutk } : {}),
    pageUri: window.location.href,
    pageName: document.title
  };
};

// Extract UTM params from URL
export const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);

  return {
    utmSource: params.get("utm_source") || "",
    utmMedium: params.get("utm_medium") || "",
    utmCampaign: params.get("utm_campaign") || "",
    utmTerm: params.get("utm_term") || "",
    utmContent: params.get("utm_content") || "",
    utmMarket: params.get("utm_market") || ""
  };
};

// The first tagged visit. It is what signup attributes the customer to, so a later visit
// never replaces it: a customer who clicks a paid ad and comes back days later through an
// organic link keeps the ad's tags.
const FIRST_TOUCH_KEY = "utmData";

// Every tagged visit, oldest first, so the backend sees the whole path.
const HISTORY_KEY = "utmHistory";
const MAX_HISTORY = 10;

// A first touch older than this no longer counts, so a visit from a long-finished
// campaign does not claim a signup months later.
const FIRST_TOUCH_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

// Set by the marketing site (fundtap.co) on the shared .fundtap.co domain, holding the
// first tagged landing there. localStorage is per-origin, so without it tags from a
// fundtap.co landing page are lost when the visitor moves on to secure.fundtap.co.
// See docs/hubspot/ATTRIBUTION.md for the snippet that sets it.
export const SHARED_UTM_COOKIE = "ft_utm";

const UTM_FIELDS = [
  "utmSource",
  "utmMedium",
  "utmCampaign",
  "utmTerm",
  "utmContent",
  "utmMarket"
];

const hasUtmValue = (touch) =>
  Boolean(touch) && UTM_FIELDS.some((field) => Boolean(touch[field]));

const sameTags = (a, b) =>
  UTM_FIELDS.every((field) => (a?.[field] || "") === (b?.[field] || ""));

const readJson = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || fallback;
  } catch {
    return fallback;
  }
};

const pickTags = (source) =>
  UTM_FIELDS.reduce(
    (tags, field) => ({ ...tags, [field]: source[field] || "" }),
    {}
  );

// The marketing site's first touch, or null when the cookie is absent or unreadable.
export const readSharedUtmCookie = () => {
  const raw = getCookie(SHARED_UTM_COOKIE);
  if (!raw) {
    return null;
  }
  try {
    const cookie = JSON.parse(decodeURIComponent(raw));
    const capturedAt = Number(cookie.ts) || Date.now();
    const touch = {
      utmSource: cookie.utm_source || "",
      utmMedium: cookie.utm_medium || "",
      utmCampaign: cookie.utm_campaign || "",
      utmTerm: cookie.utm_term || "",
      utmContent: cookie.utm_content || "",
      utmMarket: cookie.utm_market || "",
      capturedAt,
      visitTime: new Date(capturedAt).toISOString().replace(/\.\d{3}Z$/, "Z")
    };
    return hasUtmValue(touch) ? touch : null;
  } catch {
    return null;
  }
};

const isCurrentFirstTouch = (firstTouch) => {
  if (!hasUtmValue(firstTouch)) {
    return false;
  }
  // Stored before first touches carried a capture time: kept, as the only record there is.
  if (!firstTouch.capturedAt) {
    return true;
  }
  return Date.now() - firstTouch.capturedAt < FIRST_TOUCH_WINDOW_MS;
};

const recordTouch = (touch) => {
  const history = readJson(HISTORY_KEY, []);
  const alreadyRecorded = history.some(
    (visit) => visit.capturedAt === touch.capturedAt && sameTags(visit, touch)
  );
  if (!alreadyRecorded && !sameTags(history[history.length - 1], touch)) {
    history.push(touch);
    history.sort((a, b) => (a.capturedAt || 0) - (b.capturedAt || 0));
    // Trimmed from the middle, never the front: the backend attributes the signup to the
    // earliest visit, so the oldest one has to stay.
    const kept =
      history.length > MAX_HISTORY
        ? [history[0], ...history.slice(-(MAX_HISTORY - 1))]
        : history;
    localStorage.setItem(HISTORY_KEY, JSON.stringify(kept));
  }

  // The first touch is the earliest visit, which is not always the first one recorded: the
  // marketing site's cookie can carry a visit older than one already stored here.
  const firstTouch = readJson(FIRST_TOUCH_KEY, null);
  const isEarlier =
    Boolean(touch.capturedAt) &&
    Boolean(firstTouch?.capturedAt) &&
    touch.capturedAt < firstTouch.capturedAt;
  if (!isCurrentFirstTouch(firstTouch) || isEarlier) {
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
  }
};

// Records the tags this page load arrived with (call once on app load): the marketing
// site's shared cookie, then this URL's own utm_* parameters.
export const storeUTMParams = () => {
  const shared = readSharedUtmCookie();
  if (shared) {
    recordTouch(shared);
  }

  const utm = getUTMParams();
  if (hasUtmValue(utm)) {
    recordTouch({
      ...utm,
      capturedAt: Date.now(),
      visitTime: getAucklandISOString()
    });
  }
};

// Forgets this browser's tags: the first touch, the visit history and the marketing site's
// shared cookie. Called once a signup has used them and on sign-out, so the next person to
// sign up on this browser is not attributed to this one's ad.
export const clearStoredUtm = () => {
  localStorage.removeItem(FIRST_TOUCH_KEY);
  localStorage.removeItem(HISTORY_KEY);
  expireCookie(SHARED_UTM_COOKIE);
};

// The first touch's tags: what signup attributes the customer to.
export const getStoredUTMParams = () => {
  const firstTouch = readJson(FIRST_TOUCH_KEY, null);
  return isCurrentFirstTouch(firstTouch) ? pickTags(firstTouch) : {};
};

// The tagged visits for /register, each with the time it happened. The backend attributes
// the signup to the earliest of them, so visits outside the first-touch window are left out.
export const buildUtmHistory = () => {
  const history = readJson(HISTORY_KEY, [])
    .filter(hasUtmValue)
    .filter(
      (visit) =>
        !visit.capturedAt ||
        Date.now() - visit.capturedAt < FIRST_TOUCH_WINDOW_MS
    )
    .map((visit) => ({ ...pickTags(visit), visitTime: visit.visitTime }));
  if (history.length > 0) {
    return history;
  }

  // No visit recorded with its time, as for tags stored before visits were recorded.
  return [
    {
      ...pickTags(getStoredUTMParams()),
      visitTime: getAucklandISOString()
    }
  ];
};
